/**
 * @GestionalePD User national federation details UI
 */
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  Tabs,
  Tab,
  Container,
  Box,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Card,
  Grid,
  CardContent,
  TableFooter,
  TablePagination,
  useMediaQuery,
  Typography,
} from '@mui/material';
import { KeyboardArrowRight, Assignment as AssignmentIcon, GppGood as GppGoodIcon } from '@mui/icons-material';

//-----------------------------------------------------------------------------------------------
import { CustomTextField } from '../../../../utils/StyledInputs';
import { HttpErrorHandler, ErrorUI } from '../../../../utils/ErrorHandler';
import { a11yProps, MultipleTabPanel } from '../../../../utils/TabStyles';
import { EmptyPageUI, LoadingUI } from '../../../../utils/LayoutStyles';
import { TablePaginationActions } from '../../../../utils/TableStyles';
import { validatePhoneNumber } from '../../../../utils/numberValidity';
import { CustomIconButton } from '../../../../utils/Buttons';
import { PositionStatusLabel } from '../../../../utils/PositionStatusLabel';
import API from '../../../../api';

/**
 * @returns User national federation details UI
 */
export default function NationalFederationDetail() {
  // general
  const navigate = useNavigate();
  const { state } = useLocation();
  const { federationID } = state;
  const [positions, setPositions] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [hasFederation, setHasFederation] = useState(false);

  // Federation data
  const [federationName, setFederationName] = useState('');
  const [federationEmail, setFederationEmail] = useState('');
  const [federationCallCode, setFederationCallCode] = useState('');
  const [federationPhone, setFederationPhone] = useState('');
  const [federationStreet, setFederationStreet] = useState('');
  const [federationPostalCode, setFederationPostalCode] = useState('');
  const [federationCity, setFederationCity] = useState({});
  const [federationProvince, setFederationProvince] = useState({});
  const [federationRegion, setFederationRegion] = useState({});
  const [federationCountry, setFederationCountry] = useState({});

  // table pagination handler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - positions.length) : 0;

  // change page handler
  const handleChangePage = (event, newPage) => setPage(newPage);

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // tabs handler
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => setValue(newValue);

  // get organization data with expand of position by entity level
  const refreshCharges = () => {
    setErrorMessage(null);

    API.get(`/entities/${federationID}?expand=positions`)
      .then((response) => {
        setFederationName(response.data.name);
        setFederationEmail(response.data.email);
        setFederationCallCode(response.data.call_code);
        setFederationPhone(response.data.phone);
        setFederationStreet(response.data.street);
        setFederationPostalCode(response.data.zip_code);
        setFederationCity(response.data.city.name);
        setFederationProvince(response.data.province.name);
        setFederationRegion(response.data.province.region_name);
        setFederationCountry(response.data.country.name);
        generateChargeList(response.data.position);
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => setHasFederation(true));
  };

  // generate position list
  const generateChargeList = (array) => {
    positions.length = 0;
    array.forEach((subArray) => {
      if (Array.isArray(subArray)) {
        subArray.forEach((data, index) => {
          const key = `${data.row_id}${index}`;
          data.key = key;
          positions.push(data);
        });
      } else {
        positions.push(subArray);
      }
    });
  };

  // initial data loading
  useEffect(() => {
    refreshCharges();
  }, []);

  // if doesn't found federation id redirect to 404 page
  if (!federationID) return <Navigate to={{ pathname: '/404' }} />;

  return hasFederation ? (
    <Container maxWidth="xl">
      {!errorMessage ? (
        <Box sx={{ width: '100%' }}>
          <Typography
            sx={{
              height: '24px',
              width: 'fit-content',
              whiteSpace: 'nowrap',
              borderRadius: '6px',
              padding: '0 10px',
              textAlign: 'center',
              fontWeight: '700',
              fontSize: '1.1em',
            }}
          >
            {federationName}
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {/* tabs header */}
            <Tabs value={value} variant="fullWidth" onChange={handleChange} aria-label="Federation Details Page Tab">
              <Tab label="Anagrafica" {...a11yProps(0, 'double')} icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Cariche" {...a11yProps(1, 'double')} icon={<GppGoodIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* federation data */}
          <MultipleTabPanel value={value} index={0}>
            <Card sx={{ minWidth: 275 }}>
              <CardContent>
                <Grid container spacing={3}>
                  {/* Federation name */}
                  <Grid item xs={12}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-Name"
                      label="Nome"
                      defaultValue={federationName}
                    />
                  </Grid>

                  {/* Federation E-mail */}
                  <Grid item xs={12}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-Email"
                      label="E-mail"
                      type="email"
                      defaultValue={federationEmail}
                    />
                  </Grid>

                  {/* Federation Call-Code */}
                  <Grid item xs={4}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-CallCode"
                      label="Prefisso"
                      defaultValue={federationCallCode}
                    />
                  </Grid>

                  {/* Federation phone */}
                  <Grid item xs={8}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      type="tel"
                      name="Federation-Phone"
                      label="Contatto Telefonico"
                      defaultValue={federationPhone}
                      onChange={(event) => validatePhoneNumber(event, setFederationPhone)}
                    />
                  </Grid>

                  {/* Federation street */}
                  <Grid item xs={9}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-Street"
                      label="Indirizzo"
                      defaultValue={federationStreet}
                    />
                  </Grid>

                  {/* Federation Postal Code */}
                  <Grid item xs={3}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-Postal-code"
                      label="CAP"
                      defaultValue={federationPostalCode}
                    />
                  </Grid>

                  {/* federation country */}
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-Country"
                      label="Paese"
                      value={federationCountry}
                    />
                  </Grid>

                  {/* federation region */}
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-Region"
                      label="Regione"
                      value={federationRegion}
                    />
                  </Grid>

                  {/* federation province */}
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-Province"
                      label="Provincia"
                      value={federationProvince}
                    />
                  </Grid>

                  {/* federation city */}
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="Federation-City"
                      label="Comune"
                      value={federationCity}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </MultipleTabPanel>

          {/* federation charges */}
          <MultipleTabPanel value={value} index={1}>
            <Card sx={{ minWidth: 275 }}>
              {/* position table */}
              {positions.length !== 0 ? (
                <TableContainer sx={{ padding: '24px 24px 6px' }}>
                  <Table size="small" aria-label="Roles table">
                    {/* position table header */}
                    <TableHead>
                      <TableRow>
                        <TableCell>Stato</TableCell>
                        <TableCell>Nome Utente</TableCell>
                        <TableCell>Tipo Cariche</TableCell>
                        {is600Screen && <TableCell>Inizio carica</TableCell>}
                        {is600Screen && <TableCell>Fine carica</TableCell>}
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    {/* position table body */}
                    <TableBody>
                      {positions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((position) => (
                        <TableRow hover key={position.id}>
                          <TableCell>{PositionStatusLabel(position, 'icon')}</TableCell>
                          <TableCell component="th" scope="row">
                            {position.user.name} {position.user.surname}
                          </TableCell>
                          <TableCell>{position.position.name}</TableCell>
                          {is600Screen && (
                            <>
                              <TableCell>
                                {position.start_date ? dayjs(position.start_date).format('DD/MM/YYYY') : '//'}
                              </TableCell>
                              <TableCell>
                                {position.end_date ? dayjs(position.end_date).format('DD/MM/YYYY') : '//'}
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <CustomIconButton
                              size="small"
                              onClick={() =>
                                navigate('/user/organization/charge-details', {
                                  state: { chargeID: position.id },
                                })
                              }
                            >
                              <KeyboardArrowRight />
                            </CustomIconButton>
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* empty table row */}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 47 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>

                    {/* position table footer */}
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          sx={{ borderBottom: 'none' }}
                          rowsPerPageOptions={[]}
                          colSpan={6}
                          count={positions.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          SelectProps={{
                            inputProps: {
                              'aria-label': 'rows per page',
                            },
                            native: true,
                          }}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={TablePaginationActions}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              ) : (
                EmptyPageUI('Nessuna carica trovata')
              )}
            </Card>
          </MultipleTabPanel>
        </Box>
      ) : (
        ErrorUI(errorMessage)
      )}
    </Container>
  ) : (
    LoadingUI()
  );
}
